import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateInvoiceModal = ({ language, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    client: '',
    billingType: 'flat_fee',
    issueDate: new Date()?.toISOString()?.split('T')?.[0],
    dueDate: '',
    lineItems: [
      { description: '', quantity: 1, rate: 0 }
    ]
  });

  const clientOptions = [
    { 
      value: 'client_1', 
      label: language === 'fr' ? 'Fatima El Amrani' : language === 'ar' ? 'فاطمة العمراني' : 'Fatima El Amrani' 
    },
    { 
      value: 'client_2', 
      label: language === 'fr' ? 'Mohammed Benali' : language === 'ar' ? 'محمد بنعلي' : 'Mohammed Benali' 
    },
    { 
      value: 'client_3', 
      label: language === 'fr' ? 'Société TechMaroc SARL' : language === 'ar' ? 'شركة تك مغرب ذ.م.م' : 'TechMaroc SARL Company' 
    },
    { 
      value: 'client_4', 
      label: language === 'fr' ? 'Amina Tazi' : language === 'ar' ? 'أمينة التازي' : 'Amina Tazi' 
    }
  ];

  const billingTypeOptions = [
    { 
      value: 'flat_fee', 
      label: language === 'fr' ? 'Tarif forfaitaire' : language === 'ar' ? 'رسوم ثابتة' : 'Flat Fee' 
    },
    { 
      value: 'hourly', 
      label: language === 'fr' ? 'Tarif horaire' : language === 'ar' ? 'رسوم بالساعة' : 'Hourly Rate' 
    },
    { 
      value: 'per_hearing', 
      label: language === 'fr' ? 'Par audience' : language === 'ar' ? 'لكل جلسة' : 'Per Hearing' 
    }
  ];

  const labels = {
    title: language === 'fr' ? 'Créer une nouvelle facture' : language === 'ar' ? 'إنشاء فاتورة جديدة' : 'Create New Invoice',
    client: language === 'fr' ? 'Client' : language === 'ar' ? 'العميل' : 'Client',
    billingType: language === 'fr' ? 'Type de facturation' : language === 'ar' ? 'نوع الفوترة' : 'Billing Type',
    issueDate: language === 'fr' ? 'Date d\'émission' : language === 'ar' ? 'تاريخ الإصدار' : 'Issue Date',
    dueDate: language === 'fr' ? 'Date d\'échéance' : language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date',
    lineItems: language === 'fr' ? 'Services facturés' : language === 'ar' ? 'الخدمات المفوترة' : 'Line Items',
    description: language === 'fr' ? 'Description' : language === 'ar' ? 'الوصف' : 'Description',
    quantity: language === 'fr' ? 'Quantité' : language === 'ar' ? 'الكمية' : 'Quantity',
    rate: language === 'fr' ? 'Tarif (MAD)' : language === 'ar' ? 'السعر (درهم)' : 'Rate (MAD)',
    addItem: language === 'fr' ? 'Ajouter un service' : language === 'ar' ? 'إضافة خدمة' : 'Add Line Item',
    removeItem: language === 'fr' ? 'Supprimer' : language === 'ar' ? 'حذف' : 'Remove',
    subtotal: language === 'fr' ? 'Sous-total' : language === 'ar' ? 'المجموع الفرعي' : 'Subtotal',
    tax: language === 'fr' ? 'TVA (20%)' : language === 'ar' ? 'ضريبة القيمة المضافة (20%)' : 'VAT (20%)',
    total: language === 'fr' ? 'Total' : language === 'ar' ? 'الإجمالي' : 'Total',
    cancel: language === 'fr' ? 'Annuler' : language === 'ar' ? 'إلغاء' : 'Cancel',
    create: language === 'fr' ? 'Créer la facture' : language === 'ar' ? 'إنشاء الفاتورة' : 'Create Invoice'
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData?.lineItems];
    newLineItems[index][field] = value;
    setFormData({ ...formData, lineItems: newLineItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [...formData?.lineItems, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeLineItem = (index) => {
    const newLineItems = formData?.lineItems?.filter((_, i) => i !== index);
    setFormData({ ...formData, lineItems: newLineItems });
  };

  const calculateSubtotal = () => {
    return formData?.lineItems?.reduce((sum, item) => {
      return sum + (item?.quantity * item?.rate);
    }, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.2;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-MA' : 'fr-MA', {
      style: 'currency',
      currency: 'MAD'
    })?.format(amount);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-border">
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {labels?.title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label={labels?.client}
                options={clientOptions}
                value={formData?.client}
                onChange={(value) => setFormData({ ...formData, client: value })}
                required
                searchable
              />

              <Select
                label={labels?.billingType}
                options={billingTypeOptions}
                value={formData?.billingType}
                onChange={(value) => setFormData({ ...formData, billingType: value })}
                required
              />

              <Input
                label={labels?.issueDate}
                type="date"
                value={formData?.issueDate}
                onChange={(e) => setFormData({ ...formData, issueDate: e?.target?.value })}
                required
              />

              <Input
                label={labels?.dueDate}
                type="date"
                value={formData?.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e?.target?.value })}
                required
              />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-foreground">
                  {labels?.lineItems}
                </h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={addLineItem}
                >
                  {labels?.addItem}
                </Button>
              </div>

              <div className="space-y-4">
                {formData?.lineItems?.map((item, index) => (
                  <div key={index} className="bg-muted rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-5">
                        <Input
                          label={labels?.description}
                          type="text"
                          value={item?.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e?.target?.value)}
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          label={labels?.quantity}
                          type="number"
                          min="1"
                          value={item?.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e?.target?.value))}
                          required
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Input
                          label={labels?.rate}
                          type="number"
                          min="0"
                          step="0.01"
                          value={item?.rate}
                          onChange={(e) => handleLineItemChange(index, 'rate', parseFloat(e?.target?.value))}
                          required
                        />
                      </div>
                      <div className="md:col-span-2 flex items-end">
                        {formData?.lineItems?.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => removeLineItem(index)}
                            aria-label={labels?.removeItem}
                            fullWidth
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-muted rounded-lg p-4 md:p-6">
              <div className="flex justify-end">
                <div className="w-full md:w-80 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{labels?.subtotal}:</span>
                    <span className="font-medium text-foreground monospace">
                      {formatAmount(calculateSubtotal())}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{labels?.tax}:</span>
                    <span className="font-medium text-foreground monospace">
                      {formatAmount(calculateTax())}
                    </span>
                  </div>
                  <div className="flex justify-between text-base md:text-lg border-t border-border pt-2">
                    <span className="font-semibold text-foreground">{labels?.total}:</span>
                    <span className="font-bold text-foreground monospace">
                      {formatAmount(calculateTotal())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col-reverse md:flex-row gap-3 mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
            >
              {labels?.cancel}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="FileText"
              iconPosition="left"
              fullWidth
            >
              {labels?.create}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;